/**
 * Admin performance monitoring endpoints.
 *
 * All of these live on the Admin API under /api/performance, which the collectors deliberately
 * exclude from measurement — so opening this page never shows up on this page.
 */

import { ApiService } from '../apiService'
import type {
  EndpointSort,
  PerfDatabase,
  PerfEndpointRow,
  PerfOverview,
  PerfProbeResult,
  PerfProbeTarget,
  PerfProbeTargetRequest,
  PerfQueryRow,
  PerfSlowRequest,
  PerfTimePoint,
  QuerySort,
} from '../types/performance'

export const performanceApi = {
  /** Headline numbers plus the worst offenders, in one call. */
  getOverview: (hours: number, service?: string) =>
    ApiService.get<PerfOverview>('/admin/performance/overview', { hours, service }),

  getEndpoints: (params: { hours: number; service?: string; sort?: EndpointSort; limit?: number }) =>
    ApiService.get<PerfEndpointRow[]>('/admin/performance/endpoints', params),

  /** Latency over time. Omit `route` for every endpoint together. */
  getSeries: (params: { hours: number; route?: string; method?: string; service?: string }) =>
    ApiService.get<PerfTimePoint[]>('/admin/performance/endpoints/series', params),

  getQueries: (params: { hours: number; service?: string; sort?: QuerySort; limit?: number }) =>
    ApiService.get<PerfQueryRow[]>('/admin/performance/queries', params),

  getSlowRequests: (params: {
    hours: number
    service?: string
    route?: string
    minMs?: number
    limit?: number
  }) => ApiService.get<PerfSlowRequest[]>('/admin/performance/slow-requests', params),

  /** One slow request WITH the queries it issued; the list endpoint omits those. */
  getSlowRequest: (id: number) =>
    ApiService.get<PerfSlowRequest>(`/admin/performance/slow-requests/${id}`),

  getDatabase: (top = 25) =>
    ApiService.get<PerfDatabase>('/admin/performance/database', { top }),

  resetStatementStats: () =>
    ApiService.post<{ message: string }>('/admin/performance/database/reset-statements'),

  getProbes: (hours: number) =>
    ApiService.get<PerfProbeTarget[]>('/admin/performance/probes', { hours }),

  createProbe: (body: PerfProbeTargetRequest) =>
    ApiService.post<PerfProbeTarget>('/admin/performance/probes', body),

  updateProbe: (id: string, body: PerfProbeTargetRequest) =>
    ApiService.put<PerfProbeTarget>(`/admin/performance/probes/${id}`, body),

  deleteProbe: (id: string) =>
    ApiService.delete<{ message: string }>(`/admin/performance/probes/${id}`),

  /** Call one target immediately. The result is recorded like any scheduled run. */
  runProbe: (id: string) =>
    ApiService.post<PerfProbeResult>(`/admin/performance/probes/${id}/run`),

  /** Write the admin API's current partial minute out now instead of waiting for the flush. */
  flush: () => ApiService.post<{ message: string; written: number }>('/admin/performance/flush'),
}
